import { useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Building2, ChevronDown, ChevronRight, Plus, Phone, Mail, MapPin, ShieldAlert, User, FolderOpen, Trash2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useClient, useClientInvoices, useClientProjects, useDeleteClient } from "@/features/clients/hooks/useClients";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PageLoader } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { formatPhone } from "@/utils/formatPhone";
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
} from "@/features/finances/types";
import { ProjectStatusBadge } from "@/features/enterprises/components/ContractStatusBadge";
import type { ClientBranchBrief } from "@/features/clients/types";
import type { ClientInvoice } from "@/features/finances/types";
import type { Project } from "@/features/enterprises/types";

type InvoiceTab = "all" | "unpaid" | "overdue";

/** Oznaczenie „szczególnej opieki" przy oddziale / pododdziale — sama INFORMACJA.
 *  Kartę specyfiki (flagi, ustalenia, dziennik) prowadzi się w projekcie ZE,
 *  zakładka „Opieka" — tam, gdzie ludzie faktycznie pracują nad tematem. */
function CareMarker({ labels, compact }: { labels: string[]; compact?: boolean }) {
  if (labels.length === 0) return null;
  const shown = compact ? 1 : 2;
  return (
    <span
      className="inline-flex items-center gap-1 ml-2 align-middle"
      title={`Szczególna opieka: ${labels.join(" · ")} — kartę prowadzi się w projekcie ZE, zakładka „Opieka”.`}
    >
      {labels.slice(0, shown).map((l) => (
        <span
          key={l}
          className="inline-flex items-center gap-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded px-1 py-0.5"
        >
          <ShieldAlert size={10} /> {l}
        </span>
      ))}
      {labels.length > shown && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400">+{labels.length - shown}</span>
      )}
    </span>
  );
}

// Desktop table row
function BranchRow({ branch, clientId, isDirector }: { branch: ClientBranchBrief; clientId: string; isDirector: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const hasSubBranches = branch.sub_branches.length > 0;

  return (
    <>
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
        <td className="px-3 py-2 w-6">
          {hasSubBranches ? (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="inline-block w-4" />
          )}
        </td>
        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100 text-sm">
          {branch.name}
          {!branch.is_active && (
            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">(nieaktywny)</span>
          )}
          <CareMarker
            labels={branch.care_labels ?? []}
          />
        </td>
        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{branch.city || "—"}</td>
        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{branch.contact_person || "—"}</td>
        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          {branch.contact_email ? (
            <a href={`mailto:${branch.contact_email}`} className="text-[var(--color-primary)] hover:underline text-xs">
              {branch.contact_email}
            </a>
          ) : "—"}
        </td>
        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{formatPhone(branch.contact_phone)}</td>
        {isDirector && (
          <td className="px-3 py-2 text-right whitespace-nowrap">
            <Link
              to={`/clients/${clientId}/branches/${branch.id}/sub-branches/new`}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 mr-1"
              title="Dodaj pododdział"
            >
              <Plus size={11} /> Pododdział
            </Link>
            <Link
              to={`/clients/${clientId}/branches/${branch.id}/edit`}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            >
              <Edit size={11} /> Edytuj
            </Link>
          </td>
        )}
      </tr>
      {expanded && branch.sub_branches.map((sb) => (
        <tr key={sb.id} className="bg-gray-50/50 dark:bg-gray-800/30 hover:bg-gray-50 dark:hover:bg-gray-700/20">
          <td className="px-3 py-1.5 text-gray-300 dark:text-gray-600 text-xs pl-6">↳</td>
          <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">
            {sb.name}
            {!sb.is_active && (
              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">(nieaktywny)</span>
            )}
            <CareMarker
              labels={sb.care_labels ?? []}
            />
          </td>
          <td className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500">{sb.city || "—"}</td>
          <td className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500">{sb.contact_person || "—"}</td>
          <td className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500">
            {sb.contact_email ? (
              <a href={`mailto:${sb.contact_email}`} className="text-[var(--color-primary)] hover:underline">
                {sb.contact_email}
              </a>
            ) : "—"}
          </td>
          <td className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500">{formatPhone(sb.contact_phone)}</td>
          {isDirector && (
            <td className="px-3 py-1.5 text-right">
              <Link
                to={`/clients/${clientId}/branches/${branch.id}/sub-branches/${sb.id}/edit`}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              >
                <Edit size={11} /> Edytuj
              </Link>
            </td>
          )}
        </tr>
      ))}
    </>
  );
}

// Mobile branch card
function BranchCard({ branch, clientId, isDirector }: { branch: ClientBranchBrief; clientId: string; isDirector: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const hasSubBranches = branch.sub_branches.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {hasSubBranches && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
              >
                {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </button>
            )}
            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
              {branch.name}
            </span>
            {!branch.is_active && (
              <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">(nieaktywny)</span>
            )}
            <CareMarker
              compact
              labels={branch.care_labels ?? []}
            />
          </div>
          {isDirector && (
            <div className="flex gap-1 flex-shrink-0">
              <Link
                to={`/clients/${clientId}/branches/${branch.id}/sub-branches/new`}
                className="p-1.5 rounded border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Dodaj pododdział"
              >
                <Plus size={13} />
              </Link>
              <Link
                to={`/clients/${clientId}/branches/${branch.id}/edit`}
                className="p-1.5 rounded border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Edytuj"
              >
                <Edit size={13} />
              </Link>
            </div>
          )}
        </div>

        <div className="mt-2 space-y-1 pl-1">
          {branch.city && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <MapPin size={11} className="flex-shrink-0" />
              {branch.city}
            </div>
          )}
          {branch.contact_person && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <User size={11} className="flex-shrink-0" />
              {branch.contact_person}
            </div>
          )}
          {branch.contact_phone && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Phone size={11} className="flex-shrink-0" />
              <a href={`tel:${branch.contact_phone}`} className="hover:text-[var(--color-primary)]">
                {formatPhone(branch.contact_phone)}
              </a>
            </div>
          )}
          {branch.contact_email && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Mail size={11} className="flex-shrink-0" />
              <a href={`mailto:${branch.contact_email}`} className="text-[var(--color-primary)] hover:underline truncate">
                {branch.contact_email}
              </a>
            </div>
          )}
        </div>
      </div>

      {expanded && branch.sub_branches.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 divide-y divide-gray-100 dark:divide-gray-700/50">
          {branch.sub_branches.map((sb) => (
            <div key={sb.id} className="p-3 pl-5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-gray-300 dark:text-gray-600 text-xs">↳</span>
                  <span className="font-medium text-xs text-gray-800 dark:text-gray-200 truncate">{sb.name}</span>
                  {!sb.is_active && <span className="text-xs text-gray-400">(nieaktywny)</span>}
                  <CareMarker
                    compact
                    labels={sb.care_labels ?? []}
                  />
                </div>
                {isDirector && (
                  <Link
                    to={`/clients/${clientId}/branches/${branch.id}/sub-branches/${sb.id}/edit`}
                    className="flex-shrink-0 p-1.5 rounded border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <Edit size={12} />
                  </Link>
                )}
              </div>
              <div className="mt-1.5 space-y-1 pl-3">
                {sb.city && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                    <MapPin size={10} className="flex-shrink-0" /> {sb.city}
                  </div>
                )}
                {sb.contact_person && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                    <User size={10} className="flex-shrink-0" /> {sb.contact_person}
                  </div>
                )}
                {sb.contact_email && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                    <Mail size={10} className="flex-shrink-0" />
                    <a href={`mailto:${sb.contact_email}`} className="text-[var(--color-primary)] hover:underline truncate">
                      {sb.contact_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function isOverdue(inv: ClientInvoice): boolean {
  if (inv.payment_status === "paid") return false;
  const due = new Date(inv.invoice_date);
  due.setDate(due.getDate() + inv.payment_terms_days);
  return due < new Date();
}

// Mobile invoice card
function InvoiceCard({ inv }: { inv: ClientInvoice }) {
  const user = useAuthStore((s) => s.user);
  const canViewInvoicePrz =
    user?.role === "accountant" ||
    (user?.role === "director" && (!!user.is_technical_director || !!user.is_ceo));
  const overdue = isOverdue(inv);
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border overflow-hidden ${overdue ? "border-red-200 dark:border-red-900/50" : "border-gray-100 dark:border-gray-700"}`}>
      <div className={`p-3 ${overdue ? "bg-red-50/40 dark:bg-red-900/10" : ""}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            {canViewInvoicePrz ? (
              <Link
                to={`/finances/invoices-prz/${inv.id}`}
                className="font-semibold text-sm text-[var(--color-primary)] hover:underline"
              >
                {inv.invoice_number}
              </Link>
            ) : (
              <span className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                {inv.invoice_number}
              </span>
            )}
            {overdue && (
              <span className="ml-2 text-xs text-red-600 dark:text-red-400 font-medium">przeterminowana</span>
            )}
          </div>
          <Badge color={PAYMENT_STATUS_COLORS[inv.payment_status]}>
            {PAYMENT_STATUS_LABELS[inv.payment_status]}
          </Badge>
        </div>

        {inv.project_name && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-tight">{inv.project_name}</p>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div>
            <div className="text-gray-400 dark:text-gray-500 mb-0.5">Wystawiona</div>
            <div className="text-gray-700 dark:text-gray-300">{formatDate(inv.invoice_date)}</div>
          </div>
          <div>
            <div className="text-gray-400 dark:text-gray-500 mb-0.5">Termin płatności</div>
            <div className="text-gray-700 dark:text-gray-300">
              {formatDate(inv.payment_due_date)}
              <span className="ml-1 text-gray-400 dark:text-gray-500">({inv.payment_terms_days}d)</span>
            </div>
          </div>
          <div>
            <div className="text-gray-400 dark:text-gray-500 mb-0.5">Brutto</div>
            <div className="font-semibold text-gray-800 dark:text-gray-200">{formatAmount(inv.amount_gross)} PLN</div>
          </div>
          {inv.paid_amount && inv.paid_amount !== "0.00" && (
            <div>
              <div className="text-gray-400 dark:text-gray-500 mb-0.5">Zapłacono</div>
              <div className="text-gray-600 dark:text-gray-400">{formatAmount(inv.paid_amount)} PLN</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  // Clickable link to the PRZ invoice detail page — privileged finance roles only.
  const canViewInvoicePrz =
    user?.role === "accountant" ||
    (user?.role === "director" && (!!user.is_technical_director || !!user.is_ceo));
  const [invoiceTab, setInvoiceTab] = useState<InvoiceTab>("all");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");

  const { data: client, isLoading } = useClient(id!);
  usePageTitle(client?.name);
  const { data: allInvoices = [] } = useClientInvoices(id!);
  const { data: clientProjects = [] } = useClientProjects(id!);
  const deleteClient = useDeleteClient();

  function handleDelete() {
    deleteClient.mutate(id!, {
      onSuccess: () => {
        setDeleteConfirm(false);
        navigate("/clients");
      },
    });
  }

  if (isLoading) return <PageLoader />;
  if (!client) return <p className="text-gray-400 dark:text-gray-500">Nie znaleziono klienta.</p>;

  // Whether to render the "Faktury PRZ" section at all. Privileged finance
  // roles always (so they get the empty state too); anyone else only once the
  // backend actually returns invoices they may see. The backend
  // (_accessible_client_invoices, ProjectFinanceAccess) is the single source of
  // truth — a coordinator without a grant gets an empty list and no section.
  const canSeeInvoicePrz = canViewInvoicePrz || allInvoices.length > 0;

  const unpaidInvoices = allInvoices.filter((i) => i.payment_status !== "paid");
  const overdueInvoices = allInvoices.filter(isOverdue);

  const displayedInvoices =
    invoiceTab === "unpaid"
      ? unpaidInvoices
      : invoiceTab === "overdue"
      ? overdueInvoices
      : allInvoices;

  const totalUnpaid = unpaidInvoices.reduce(
    (sum, i) => sum + parseFloat(i.amount_gross) - parseFloat(i.paid_amount || "0"),
    0
  );
  const totalOverdue = overdueInvoices.reduce(
    (sum, i) => sum + parseFloat(i.amount_gross) - parseFloat(i.paid_amount || "0"),
    0
  );

  const addr = client.correspondence_address ?? client.registered_address;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800"
        >
          <ArrowLeft size={14} /> Klienci
        </Link>
        {isDirector && (
          <div className="flex items-center gap-2">
            <Link
              to={`/clients/${id}/edit`}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <Edit size={14} /> Edytuj
            </Link>
            <button
              onClick={() => { setDeleteConfirm(true); setDeleteInput(""); }}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 size={14} /> Usuń
            </button>
          </div>
        )}
      </div>


      {deleteConfirm && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Usuń klienta</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Klient zostanie oznaczony jako <strong>nieaktywny</strong>. Powiązane przedsięwzięcia i oddziały pozostaną w systemie.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Aby potwierdzić, wpisz:{" "}
              <strong className="text-gray-900 dark:text-gray-100 font-mono">usuń klienta</strong>
            </p>
            <input
              autoFocus
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="usuń klienta"
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
              >
                Anuluj
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteInput !== "usuń klienta" || deleteClient.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleteClient.isPending ? "Usuwanie…" : "Usuń klienta"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
          <Building2 size={20} className="text-[var(--color-primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">{client.name}</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            NIP: <span className="font-mono">{client.nip}</span>
            {client.short_name && <span className="ml-3 text-gray-400 dark:text-gray-500">{client.short_name}</span>}
          </p>
        </div>
        <div className="flex-shrink-0">
          <Badge color={client.is_active ? "green" : "gray"}>
            {client.is_active ? "Aktywny" : "Nieaktywny"}
          </Badge>
        </div>
      </div>

      {/* Info cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg sm:rounded-none">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Dane rejestrowe</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">NIP</dt>
              <dd className="font-mono text-gray-800 dark:text-gray-200">{client.nip}</dd>
            </div>
            {client.regon && (
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">REGON</dt>
                <dd className="font-mono text-gray-800 dark:text-gray-200">{client.regon}</dd>
              </div>
            )}
            {client.krs && (
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">KRS</dt>
                <dd className="font-mono text-gray-800 dark:text-gray-200">{client.krs}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">VAT</dt>
              <dd>{client.is_vat_payer ? "Vatowiec" : "Nievatowiec"}</dd>
            </div>
            {client.eu_vat_number && (
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">VAT UE</dt>
                <dd className="font-mono text-gray-800 dark:text-gray-200">{client.eu_vat_number}</dd>
              </div>
            )}
            {addr && (
              <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                <dt className="text-gray-500 dark:text-gray-400 mb-1">Adres</dt>
                <dd className="text-gray-800 dark:text-gray-200 text-xs leading-relaxed">
                  {addr.street} {addr.building_number}
                  {addr.apartment_number ? `/${addr.apartment_number}` : ""}
                  <br />
                  {addr.postal_code} {addr.city}
                  {addr.country && addr.country !== "Polska" && <><br />{addr.country}</>}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg sm:rounded-none">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Kontakt</h2>
          <dl className="space-y-2 text-sm">
            {client.contact_person && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Osoba kontaktowa</dt>
                <dd className="text-gray-800 dark:text-gray-200">{client.contact_person}</dd>
              </div>
            )}
            {client.contact_email && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">E-mail</dt>
                <dd>
                  <a href={`mailto:${client.contact_email}`} className="text-[var(--color-primary)] hover:underline text-xs break-all">
                    {client.contact_email}
                  </a>
                </dd>
              </div>
            )}
            {client.contact_phone && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Telefon</dt>
                <dd className="text-gray-800 dark:text-gray-200">{formatPhone(client.contact_phone)}</dd>
              </div>
            )}
            {client.website && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">WWW</dt>
                <dd>
                  <a
                    href={client.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--color-primary)] hover:underline text-xs break-all"
                  >
                    {client.website}
                  </a>
                </dd>
              </div>
            )}
            {!client.contact_person && !client.contact_email && !client.contact_phone && (
              <p className="text-gray-400 dark:text-gray-500 text-xs">Brak danych kontaktowych.</p>
            )}
          </dl>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg sm:rounded-none">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Dane bankowe</h2>
          <dl className="space-y-2 text-sm">
            {client.bank_account_number ? (
              <>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 text-xs">Nr rachunku</dt>
                  <dd className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all">{client.bank_account_number}</dd>
                </div>
                {client.bank_name && (
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 text-xs">Bank</dt>
                    <dd className="text-gray-800 dark:text-gray-200">{client.bank_name}</dd>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-400 dark:text-gray-500 text-xs">Brak danych bankowych.</p>
            )}
          </dl>
          {client.notes && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Uwagi</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Branches section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Oddziały</h2>
          {isDirector && (
            <Link
              to={`/clients/${id}/branches/new`}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            >
              <Plus size={12} /> Dodaj oddział
            </Link>
          )}
        </div>
        {client.branches_brief.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak oddziałów.</p>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="sm:hidden space-y-2">
              {client.branches_brief.map((branch) => (
                <BranchCard key={branch.id} branch={branch} clientId={id!} isDirector={isDirector} />
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[600px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-3 py-2 w-6" />
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Miasto</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kontakt</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">E-mail</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Telefon</th>
                    {isDirector && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {client.branches_brief.map((branch) => (
                    <BranchRow key={branch.id} branch={branch} clientId={id!} isDirector={isDirector} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Przedsięwzięcia */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <FolderOpen size={16} className="text-[var(--color-primary)]" />
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Przedsięwzięcia</h2>
          <span className="text-xs text-gray-400 dark:text-gray-500">({clientProjects.length})</span>
        </div>
        {clientProjects.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak przedsięwzięć.</p>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="sm:hidden space-y-2">
              {clientProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="block bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 p-3 hover:border-[var(--color-primary)]/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-mono text-xs font-semibold text-[var(--color-primary)]">{project.short_name}</span>
                    <ProjectStatusBadge project={project} />
                  </div>
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{project.name}</p>
                  {project.coordinators_detail.length > 0 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {project.coordinators_detail.map((c) => `${c.first_name} ${c.last_name}`).join(", ")}
                    </p>
                  )}
                </Link>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[500px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Skrót</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Koordynatorzy</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {clientProjects.map((project: Project) => (
                    <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-[var(--color-primary)]">
                        <Link to={`/projects/${project.id}`} className="hover:underline">{project.short_name}</Link>
                      </td>
                      <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                        <Link to={`/projects/${project.id}`} className="hover:underline">{project.name}</Link>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {project.coordinators_detail.length > 0
                          ? project.coordinators_detail.map((c) => `${c.first_name} ${c.last_name}`).join(", ")
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <ProjectStatusBadge project={project} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Faktury PRZ — only for users with financial access to this client's projects */}
      {canSeeInvoicePrz && (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Faktury PRZ</h2>
        {unpaidInvoices.length > 0 && (
          <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full px-2 py-0.5 font-medium whitespace-nowrap">
            {unpaidInvoices.length} nieopłaconych · {formatAmount(totalUnpaid.toString())} PLN
          </span>
        )}
        {overdueInvoices.length > 0 && (
          <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full px-2 py-0.5 font-medium whitespace-nowrap">
            {overdueInvoices.length} przeterminowanych · {formatAmount(totalOverdue.toString())} PLN
          </span>
        )}
      </div>

      {/* Tab filter */}
      <div className="flex gap-1 mb-3 flex-wrap">
        {(["all", "unpaid", "overdue"] as InvoiceTab[]).map((tab) => {
          const labels: Record<InvoiceTab, string> = {
            all: "Wszystkie",
            unpaid: `Nieopłacone (${unpaidInvoices.length})`,
            overdue: `Przeterminowane (${overdueInvoices.length})`,
          };
          return (
            <button
              key={tab}
              onClick={() => setInvoiceTab(tab)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                invoiceTab === tab
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              }`}
            >
              {labels[tab]}
            </button>
          );
        })}
      </div>

      {displayedInvoices.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak faktur.</p>
      ) : (
        <>
          {/* Mobile: invoice cards */}
          <div className="sm:hidden space-y-2">
            {displayedInvoices.map((inv) => (
              <InvoiceCard key={inv.id} inv={inv} />
            ))}
          </div>
          {/* Desktop: invoice table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr faktury</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wystawiona</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Termin płatności</th>
                  <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Brutto</th>
                  <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zapłacono</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {displayedInvoices.map((inv) => {
                  const overdue = isOverdue(inv);
                  return (
                    <tr key={inv.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${overdue ? "bg-red-50/40" : ""}`}>
                      <td className="px-3 py-2">
                        {canViewInvoicePrz ? (
                          <Link
                            to={`/finances/invoices-prz/${inv.id}`}
                            className="font-medium text-[var(--color-primary)] hover:underline"
                          >
                            {inv.invoice_number}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-700 dark:text-gray-300">{inv.invoice_number}</span>
                        )}
                        {overdue && (
                          <span className="ml-1.5 text-xs text-red-600 font-medium">przeterminowana</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{inv.project_name}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{formatDate(inv.invoice_date)}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(inv.payment_due_date)}
                        <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({inv.payment_terms_days}d)</span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-gray-200">
                        {formatAmount(inv.amount_gross)} PLN
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 text-xs">
                        {inv.paid_amount && inv.paid_amount !== "0.00"
                          ? formatAmount(inv.paid_amount)
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge color={PAYMENT_STATUS_COLORS[inv.payment_status]}>
                          {PAYMENT_STATUS_LABELS[inv.payment_status]}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}
